import _ from 'lodash';
import type Scroll from 'quill/blots/scroll';
import React from 'react';
import BorderedIcon from '../../components/BorderedIcon';
import ExternalBrandLogo from '../../components/ExternalBrandLogo';
import attachmentIcon from '../../icons/non-prefilled/16x16/attachment.svg';
import PostIcon from '../../post/PostIcon';
import getFeature, { isFeatureEnabled } from '../../utils/features';
import type { FuzzySearchMatch } from '../../utils/search/fuzzySearch';
import fuzzySearch, { FuzzySearchIndex } from '../../utils/search/fuzzySearch';
import IFrame from '../formats/IFrame';
import {
  blockquoteIcon,
  boldIcon,
  buttonIcon,
  codeIcon,
  dividerIcon,
  equationIcon,
  flowchartIcon,
  h1Icon,
  h2Icon,
  h3Icon,
  hintErrorIcon,
  hintInfoIcon,
  hintSuccessIcon,
  hintWarningIcon,
  iframeIcon,
  imageIcon,
  italicIcon,
  listBulletedIcon,
  listCheckedIcon,
  listNumberedIcon,
  profileIcon,
  strikethroughIcon,
  tableIcon,
} from './editorIcons';

export enum CommandMenuMatchKey {
  Label = 'data.label',
  Secondary = 'data.secondary',
  Keywords = 'data.keywords',
}

export const INSERT_TYPES = {
  IMAGE: 'image',
  ATTACHMENT: 'attachment',
  DIVIDER: 'divider',
  TABLE_EMBED: 'table-embed',
  CODE_BLOCK: 'code-block',
  MENTION_USER: 'mention-user',
  MENTION_POST: 'mention-post',
  FLOWCHART: 'flowchart',
  EQUATION: 'equation',
  BUTTON: 'button',
} as const;

export const Heading1Item = {
  id: 'heading1',
  data: {
    label: 'Heading 1',
    icon: <BorderedIcon>{h1Icon}</BorderedIcon>,
  },
};

export const Heading2Item = {
  id: 'heading2',
  data: {
    label: 'Heading 2',
    icon: <BorderedIcon>{h2Icon}</BorderedIcon>,
  },
};

export const Heading3Item = {
  id: 'heading3',
  data: {
    label: 'Heading 3',
    icon: <BorderedIcon>{h3Icon}</BorderedIcon>,
  },
};

export const BulletedListItem = {
  id: 'bulleted',
  data: {
    label: 'Bulleted list',
    icon: <BorderedIcon>{listBulletedIcon}</BorderedIcon>,
  },
  tag: 'ul',
};

export const NumberedListItem = {
  id: 'numbered',
  data: {
    label: 'Numbered list',
    icon: <BorderedIcon>{listNumberedIcon}</BorderedIcon>,
  },
  tag: 'ol',
};

export const ChecklistItem = {
  id: 'checklist',
  data: {
    label: 'Checklist',
    icon: <BorderedIcon>{listCheckedIcon}</BorderedIcon>,
  },
};

const CodeBlockItem = {
  id: 'codeblock',
  data: {
    label: 'Code block',
    icon: <BorderedIcon>{codeIcon}</BorderedIcon>,
  },
};

export const BlockquoteItem = {
  id: 'blockquote',
  data: {
    label: 'Blockquote',
    icon: <BorderedIcon>{blockquoteIcon}</BorderedIcon>,
  },
};

export const HintWarningItem = {
  id: 'hint-warning',
  data: {
    label: 'Hint · Warning',
    icon: <BorderedIcon>{hintWarningIcon}</BorderedIcon>,
  },
  tag: 'yellow',
};

export const HintDangerItem = {
  id: 'hint-danger',
  data: {
    label: 'Hint · Danger',
    icon: <BorderedIcon>{hintErrorIcon}</BorderedIcon>,
  },
  tag: ['red', 'error'],
};

export const HintSuccessItem = {
  id: 'hint-success',
  data: {
    label: 'Hint · Success',
    icon: <BorderedIcon>{hintSuccessIcon}</BorderedIcon>,
  },
  tag: 'green',
};

export const HintInfoItem = {
  id: 'hint-info',
  data: {
    label: 'Hint · Info',
    icon: <BorderedIcon>{hintInfoIcon}</BorderedIcon>,
  },
  tag: 'blue',
};

type Item = {
  id: string;
  tag?: string | string[];
  data: {
    label: string;
    secondary?: string;
    keywords?: string[];
    icon: React.ReactNode;
  };
};

type Action = (index: number) => void;

const getSlashCommandMenuSections = ({
  scroll,
  query,
  allowedEmbedHosts,
  onFormat,
  onInsert,
  onShowTooltip,
  publicView,
}: {
  scroll: Scroll;
  query: string;
  allowedEmbedHosts: string[];
  onFormat: (format: Record<string, boolean | string | number>) => void;
  onInsert: (
    type: (typeof INSERT_TYPES)[keyof typeof INSERT_TYPES],
    index: number,
  ) => void;
  onShowTooltip: (value: string, index: number) => void;
  publicView: boolean;
}) => {
  const actions = new Map<string, (index: number) => void>();
  const bindFormatAction = (
    item: Item,
    format: string,
    value: boolean | string | number,
  ) => {
    if (!scroll.query(format)) return null;
    actions.set(item.id, () => onFormat({ [format]: value }));
    return item;
  };

  const bindAction = (item: Item, action: Action) => {
    actions.set(item.id, action);
    return item;
  };

  const getAction = (id: string) => actions.get(id);

  const thirdPartyItem = ({
    label,
    secondary,
    keywords,
    targetName,
    ...props
  }: {
    label: string;
    secondary: string;
    keywords?: string[];
    targetName: string;
  } & React.ComponentProps<typeof ExternalBrandLogo>) => {
    const { brand, product } = props;
    const id = ['embed', brand, product, secondary].filter(Boolean).join('-');
    return bindAction(
      {
        id,
        data: {
          label,
          secondary,
          keywords,
          icon: (
            <BorderedIcon>
              <ExternalBrandLogo height={16} {...props} />
            </BorderedIcon>
          ),
        },
      },
      (index) => onShowTooltip(`Paste a link to ${targetName}`, index),
    );
  };

  const iframeActions =
    allowedEmbedHosts.length > 0 && scroll.query(IFrame.blotName)
      ? [
          bindAction(
            {
              id: 'iframe',
              data: {
                label: 'iframe',
                secondary: 'Embed',
                icon: <BorderedIcon>{iframeIcon}</BorderedIcon>,
              },
            },
            (index) => {
              onShowTooltip('Paste iframe code to embed', index);
            },
          ),
        ]
      : [];

  const isSupportingCodeBlockItem = scroll.query('code-embed');

  const sections = _.compact([
    {
      label: 'formatting',
      items: [
        bindFormatAction(
          {
            id: 'bold',
            data: {
              label: 'Bold',
              icon: <BorderedIcon>{boldIcon}</BorderedIcon>,
            },
          },
          'bold',
          true,
        ),
        bindFormatAction(
          {
            id: 'italic',
            data: {
              label: 'Italic',
              icon: <BorderedIcon>{italicIcon}</BorderedIcon>,
            },
          },
          'italic',
          true,
        ),
        bindFormatAction(
          {
            id: 'strikethrough',
            data: {
              label: 'Strikethrough',
              icon: <BorderedIcon>{strikethroughIcon}</BorderedIcon>,
            },
          },
          'strike',
          true,
        ),
        bindFormatAction(
          {
            id: 'inline-code',
            data: {
              label: 'Inline code',
              icon: <BorderedIcon>{codeIcon}</BorderedIcon>,
            },
          },
          'code',
          true,
        ),
        bindFormatAction(Heading1Item, 'header', 1),
        bindFormatAction(Heading2Item, 'header', 2),
        bindFormatAction(Heading3Item, 'header', 3),
        bindFormatAction(BulletedListItem, 'list', 'bullet'),
        bindFormatAction(NumberedListItem, 'list', 'ordered'),
        bindFormatAction(ChecklistItem, 'list', 'unchecked'),
        isSupportingCodeBlockItem &&
          bindAction(CodeBlockItem, (index) =>
            onInsert(INSERT_TYPES.CODE_BLOCK, index),
          ),
        bindFormatAction(BlockquoteItem, 'blockquote', true),
        bindFormatAction(HintWarningItem, 'hint', 'warning'),
        bindFormatAction(HintDangerItem, 'hint', 'error'),
        bindFormatAction(HintSuccessItem, 'hint', 'success'),
        bindFormatAction(HintInfoItem, 'hint', 'info'),
      ],
    },
    {
      label: 'elements',
      items: [
        scroll.query('table-embed') &&
          bindAction(
            {
              id: 'table-embed',
              data: {
                label: 'Table',
                icon: <BorderedIcon>{tableIcon}</BorderedIcon>,
              },
            },
            (index) => onInsert(INSERT_TYPES.TABLE_EMBED, index),
          ),
        scroll.query('image') &&
          bindAction(
            {
              id: 'image',
              data: {
                label: 'Image',
                icon: <BorderedIcon>{imageIcon}</BorderedIcon>,
              },
              tag: 'img',
            },
            (index) => onInsert(INSERT_TYPES.IMAGE, index),
          ),
        scroll.query('attachment') &&
          bindAction(
            {
              id: 'file',
              data: {
                label: 'File attachment',
                icon: <BorderedIcon>{attachmentIcon}</BorderedIcon>,
              },
            },
            (index) => onInsert(INSERT_TYPES.ATTACHMENT, index),
          ),
        scroll.query('hr') &&
          bindAction(
            {
              id: 'divider',
              data: {
                label: 'Divider',
                icon: <BorderedIcon>{dividerIcon}</BorderedIcon>,
              },
              tag: 'hr',
            },
            (index) => onInsert(INSERT_TYPES.DIVIDER, index),
          ),
        isSupportingCodeBlockItem &&
          bindAction(
            {
              id: 'flowchart',
              data: {
                label: 'Flowchart',
                icon: <BorderedIcon>{flowchartIcon}</BorderedIcon>,
              },
              tag: 'mermaid',
            },
            (index) => onInsert(INSERT_TYPES.FLOWCHART, index),
          ),
        isSupportingCodeBlockItem &&
          bindAction(
            {
              id: 'equation',
              data: {
                label: 'Equation',
                icon: <BorderedIcon>{equationIcon}</BorderedIcon>,
              },
              tag: ['latex', 'katex', 'tex'],
            },
            (index) => onInsert(INSERT_TYPES.EQUATION, index),
          ),
        isFeatureEnabled('button_blot') &&
          scroll.query('button') &&
          bindAction(
            {
              id: 'button',
              data: {
                label: 'Button',
                icon: <BorderedIcon>{buttonIcon}</BorderedIcon>,
              },
            },
            (index) => onInsert(INSERT_TYPES.BUTTON, index),
          ),
      ],
    },
    !publicView && {
      label: 'mentions',
      items: [
        bindAction(
          {
            id: 'mention-user',
            data: {
              label: 'Mention a teammate',
              icon: <BorderedIcon>{profileIcon}</BorderedIcon>,
            },
          },
          (index) => onInsert(INSERT_TYPES.MENTION_USER, index),
        ),
        bindAction(
          {
            id: 'mention-post',
            data: {
              label: 'Mention a post',
              icon: <PostIcon />,
            },
          },
          (index) => onInsert(INSERT_TYPES.MENTION_POST, index),
        ),
        thirdPartyItem({
          brand: 'asana',
          label: 'Asana',
          secondary: 'Task',
          targetName: 'an Asana task',
        }),
        thirdPartyItem({
          brand: 'clickup',
          label: 'ClickUp',
          secondary: 'Task',
          targetName: 'a ClickUp task',
        }),
        thirdPartyItem({
          brand: 'shortcut',
          label: 'Shortcut',
          secondary: 'Story',
          targetName: 'a Shortcut story',
        }),
        thirdPartyItem({
          brand: 'freshdesk',
          label: 'Freshdesk',
          secondary: 'Ticket',
          targetName: 'a Freshdesk ticket',
        }),
        thirdPartyItem({
          brand: 'github',
          label: 'GitHub',
          secondary: 'Issue',
          targetName: 'a GitHub issue',
        }),
        thirdPartyItem({
          brand: 'guru',
          label: 'Guru',
          secondary: 'Card',
          targetName: 'a Guru card',
        }),
        thirdPartyItem({
          brand: 'height',
          label: 'Height',
          secondary: 'Task',
          targetName: 'a Height task',
        }),
        thirdPartyItem({
          brand: 'atlassian',
          label: 'Jira',
          secondary: 'Issue',
          targetName: 'a JIRA issue',
        }),
        thirdPartyItem({
          brand: 'monday',
          label: 'Monday',
          secondary: 'Item',
          targetName: 'a Monday item',
        }),
        thirdPartyItem({
          brand: 'trello',
          label: 'Trello',
          secondary: 'Card',
          targetName: 'a Trello card',
        }),
        thirdPartyItem({
          brand: 'wrike',
          label: 'Wrike',
          secondary: 'Task',
          targetName: 'a Wrike task',
        }),
        thirdPartyItem({
          brand: 'todoist',
          label: 'Todoist',
          secondary: 'Task',
          targetName: 'a Todoist task',
        }),
        ...(getFeature('zendesk')
          ? [
              thirdPartyItem({
                brand: 'zendesk',
                label: 'Zendesk',
                secondary: 'Ticket',
                targetName: 'a Zendesk ticket',
              }),
            ]
          : []),
      ],
    },
    !publicView && {
      label: 'embeds',
      items: scroll.query('embed')
        ? [
            thirdPartyItem({
              brand: 'airtable',
              label: 'Airtable',
              secondary: 'Base',
              targetName: 'an Airtable base',
            }),
            thirdPartyItem({
              brand: 'canva',
              label: 'Canva',
              secondary: 'Design',
              targetName: 'a Canva design',
            }),
            thirdPartyItem({
              brand: 'coda',
              label: 'Coda',
              secondary: 'Document',
              targetName: 'a Coda document',
            }),
            thirdPartyItem({
              brand: 'codepen',
              label: 'CodePen',
              secondary: 'Pen',
              targetName: 'a CodePen pen',
            }),
            thirdPartyItem({
              brand: 'drawio',
              label: 'Draw.io',
              secondary: 'Diagram',
              targetName: 'a Draw.io diagram',
            }),
            thirdPartyItem({
              brand: 'dropbox',
              label: 'Dropbox',
              secondary: 'Image or video',
              targetName: 'an image or video in Dropbox',
            }),
            thirdPartyItem({
              brand: 'figma',
              label: 'Figma',
              secondary: 'Project',
              targetName: 'a Figma project',
            }),
            thirdPartyItem({
              brand: 'gliffy',
              label: 'Gliffy',
              secondary: 'Diagram',
              targetName: 'a Gliffy diagram',
            }),
            thirdPartyItem({
              brand: 'google',
              product: 'calendar',
              label: 'Google Calendar',
              secondary: 'Calendar',
              targetName: 'a calendar in Google Calendar',
            }),
            thirdPartyItem({
              brand: 'google',
              product: 'doc',
              label: 'Google Docs',
              secondary: 'Document',
              targetName: 'a document in Google Docs',
            }),
            thirdPartyItem({
              brand: 'google',
              product: 'drive',
              label: 'Google Drive',
              secondary: 'File',
              targetName: 'a Google Drive file',
            }),
            thirdPartyItem({
              brand: 'google',
              product: 'sheet',
              label: 'Google Sheets',
              secondary: 'Spreadsheet',
              targetName: 'a spreadsheet in Google Sheets',
            }),
            thirdPartyItem({
              brand: 'google',
              product: 'slide',
              label: 'Google Slides',
              secondary: 'Presentation',
              targetName: 'a presentation in Google Slides',
            }),
            thirdPartyItem({
              brand: 'invision',
              label: 'InVision',
              secondary: 'Prototype',
              targetName: 'an InVision prototype',
            }),
            thirdPartyItem({
              brand: 'iorad',
              label: 'Iorad',
              secondary: 'Tutorial',
              targetName: 'an Iorad tutorial',
            }),
            thirdPartyItem({
              brand: 'jotform',
              label: 'Jotform',
              secondary: 'Form',
              targetName: 'a Jotform form',
            }),
            thirdPartyItem({
              brand: 'jsfiddle',
              label: 'JSFiddle',
              secondary: 'Fiddle',
              targetName: 'a JSFiddle fiddle',
            }),
            thirdPartyItem({
              brand: 'loom',
              label: 'Loom',
              secondary: 'Video',
              targetName: 'a Loom video',
            }),
            thirdPartyItem({
              brand: 'lucidchart',
              label: 'Lucid',
              secondary: 'Chart or board',
              targetName: 'a Lucid chart or board',
            }),
            thirdPartyItem({
              brand: 'ludus',
              label: 'Ludus',
              secondary: 'Slideshow',
              targetName: 'a Ludus Slideshow',
            }),
            thirdPartyItem({
              brand: 'microsoft',
              product: '365',
              label: 'Microsoft 365',
              secondary: 'Word, Excel, or PowerPoint',
              targetName: 'a document',
              keywords: ['office'],
            }),
            thirdPartyItem({
              brand: 'miro',
              label: 'Miro',
              secondary: 'Whiteboard',
              targetName: 'a Miro whiteboard',
            }),
            thirdPartyItem({
              brand: 'mode',
              label: 'Mode',
              secondary: 'Dashboard',
              targetName: 'a Mode dashboard',
            }),
            thirdPartyItem({
              brand: 'monday',
              label: 'Monday',
              secondary: 'Board view',
              targetName: 'a Monday board view',
            }),
            thirdPartyItem({
              brand: 'otter',
              label: 'Otter',
              secondary: 'Conversation',
              targetName: 'an Otter conversation',
            }),
            thirdPartyItem({
              brand: 'pingboard',
              label: 'Pingboard',
              secondary: 'Chart',
              targetName: 'a Pingboard chart',
            }),
            thirdPartyItem({
              brand: 'pitch',
              label: 'Pitch',
              secondary: 'Presentation',
              targetName: 'a Pitch Presentation',
            }),
            thirdPartyItem({
              brand: 'rows',
              label: 'Rows.com',
              secondary: 'Spreadsheet',
              targetName: 'a Rows.com spreadsheet',
            }),
            thirdPartyItem({
              brand: 'slideshare',
              label: 'SlideShare',
              secondary: 'Slide',
              targetName: 'a SlideShare slide',
            }),
            thirdPartyItem({
              brand: 'smartsheet',
              label: 'Smartsheet',
              secondary: 'Document',
              targetName: 'a Smartsheet document',
            }),
            thirdPartyItem({
              brand: 'tableau',
              label: 'Tableau',
              secondary: 'View',
              targetName: 'a Tableau view',
            }),
            thirdPartyItem({
              brand: 'typeform',
              label: 'Typeform',
              secondary: 'Form',
              targetName: 'a Typeform form',
            }),
            thirdPartyItem({
              brand: 'vimeo',
              label: 'Vimeo',
              secondary: 'Video',
              targetName: 'a Vimeo video',
            }),
            thirdPartyItem({
              brand: 'whimsical',
              label: 'Whimsical',
              secondary: 'File',
              targetName: 'a Whimsical file',
            }),
            thirdPartyItem({
              brand: 'wistia',
              label: 'Wistia',
              secondary: 'Video',
              targetName: 'a Wistia video',
            }),
            thirdPartyItem({
              brand: 'twitter',
              label: 'X',
              secondary: 'Tweet',
              targetName: 'a tweet',
              keywords: ['twitter'],
            }),
            thirdPartyItem({
              brand: 'google',
              product: 'youtube',
              label: 'Youtube',
              secondary: 'Video',
              targetName: 'a Youtube video',
            }),
            ...iframeActions,
          ]
        : [...iframeActions],
    },
  ]);

  const filteredSections = sections
    .map((section) => {
      const items = section.items.filter((item): item is Item => !!item);
      const filteredItems: (Item & {
        data: { matches?: FuzzySearchMatch[] };
      })[] = section.label.toUpperCase().startsWith(query)
        ? items
        : fuzzySearch(
            query,
            new FuzzySearchIndex(items, [
              CommandMenuMatchKey.Label,
              CommandMenuMatchKey.Secondary,
              CommandMenuMatchKey.Keywords,
              'tag',
            ]),
            {
              weights: {
                [CommandMenuMatchKey.Label]: 1,
                [CommandMenuMatchKey.Secondary]: 0.1,
                [CommandMenuMatchKey.Keywords]: 0.1,
                tag: 0.01,
              },
            },
          )
            .toSorted((a, b) =>
              a.score === b.score ? a.refIndex - b.refIndex : b.score - a.score,
            )
            .map(({ item: { id, data }, matches }) => ({
              id,
              data: { ...data, matches },
            }));

      return {
        ...section,
        items: filteredItems,
      };
    })
    .filter(({ items }) => items.length > 0);

  return { sections: filteredSections, getAction };
};

export default getSlashCommandMenuSections;
